import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { useUserId } from '@/auth/UserIdContext';
import { useImageUpload } from '@/hooks/useImageUpload';
import { trackEvent } from '@/utils/fullstory';

/**
 * Upload a picture of a post and ask the agent to save it as an inspiration.
 *
 * The Content space has no agent of its own, so the upload has to enter chat to
 * be read. It sends three shipped actions, in this order, which matters:
 *
 * 1. `do_create_chat` with NO initial message and NO linked item. A linked item
 *    would dedupe against an artifact that does not exist yet, and an initial
 *    message would force a turn before the image is attached.
 * 2. `report_user_uploaded_image`, which targets the chat the first action just
 *    made current. It throws when no chat is current, so it cannot go first.
 * 3. the request that makes the agent take its turn.
 *
 * One upload interaction sends one sequence, however many files it carried.
 */
export const useInspirationUpload = () => {
	const bridgeApi = useBridgeApi();
	const userId = useUserId();
	const { handleFile, isUploading, error } = useImageUpload(userId);

	const uploadAndSave = useCallback(
		async (file: File): Promise<boolean> => {
			const url = await handleFile(file);
			if (!url) return false;

			trackEvent('Inspiration Uploaded');
			bridgeApi.sendAction({ type: 'do_create_chat' });
			bridgeApi.sendAction({
				type: 'report_user_uploaded_image',
				image_url: url,
			});
			bridgeApi.sendAction({
				type: 'do_add_user_message_and_force_response',
				content:
					'Save this design as an inspiration. Read what makes it look the ' +
					'way it does.',
			});
			return true;
		},
		[bridgeApi, handleFile],
	);

	return { uploadAndSave, isUploading, error };
};
